import { ATOL_DRIVER_ID, driversNeedingAction, findDriver } from './drivers'
import { cashSale, kassaEnabled } from './kassas'
import { linkPhase } from '@krp/client-runtime'
import { categoryDriverReady, hasConnectableCandidate } from './scan'
import type { EquipmentSnapshot, HardwareTabId } from './types'

/** Табы окна «Оборудование» (п. 2.2) по порядку установки. Таб — адрес: `/hardware/<tab>`. */
export const HARDWARE_TABS: readonly HardwareTabId[] = ['drivers', 'kassas', 'terminals']

export const TAB_TITLES: Record<HardwareTabId, string> = { drivers: 'Драйверы', kassas: 'Кассы', terminals: 'Терминалы' }

export const isHardwareTab = (value: string | undefined): value is HardwareTabId =>
  value !== undefined && (HARDWARE_TABS as readonly string[]).includes(value)

export type TabGlyph = '✓' | '!' | '✕' | '…' | '–'

export interface TabStateVm {
  id: HardwareTabId
  title: string
  glyph: TabGlyph
  /** Слово состояния для значка и диктора: не только цвет. */
  word: string
  /** Для диктора целиком: «Кассы: нельзя продавать». */
  label: string
  /** Число настроенных устройств (у «Драйверов» — драйверов, требующих действия, если оно больше нуля). */
  badge: number | null
}

const WORD: Record<TabGlyph, string> = {
  '✓': 'готово',
  '!': 'требует внимания',
  '✕': 'нельзя продавать',
  '…': 'проверяем',
  '–': 'недоступно',
}

function make(id: HardwareTabId, glyph: TabGlyph, badge: number | null): TabStateVm {
  const word = WORD[glyph]
  return { id, title: TAB_TITLES[id], glyph, word, label: `${TAB_TITLES[id]}: ${word}`, badge }
}

/** Значок и слово таба по снимку. Нет агента — все табы «недоступно» (п. 3.1), пока ищем — «проверяем». */
export function tabState(id: HardwareTabId, snapshot: EquipmentSnapshot): TabStateVm {
  const phase = linkPhase(snapshot.link, snapshot.now)
  if (phase === 'searching') return make(id, '…', null)
  if (phase !== 'connected' && phase !== 'outdated') return make(id, '–', null)

  if (id === 'drivers') {
    const needing = driversNeedingAction(snapshot)
    if (needing > 0) return make(id, '!', needing)
    return make(id, findDriver(snapshot, ATOL_DRIVER_ID) === null ? '…' : '✓', null)
  }

  if (id === 'kassas') {
    if (findDriver(snapshot, ATOL_DRIVER_ID)?.state !== 'ready') return make(id, '–', null)
    if (!kassaEnabled(snapshot)) return make(id, '!', null)
    const sale = cashSale(snapshot)
    if (sale.state === 'waiting') return make(id, '…', 1)
    if (sale.state === 'blocked') return make(id, '✕', 1)
    return make(id, sale.state === 'warn' ? '!' : '✓', 1)
  }

  // terminals
  if (phase === 'outdated') return make(id, '–', null)
  const items = snapshot.terminals?.items ?? []
  if (items.length === 0) return make(id, hasConnectableCandidate(snapshot, 'payment-terminal') ? '!' : '–', null)
  const cards = items.map((item) => ({ item, check: snapshot.terminalChecks[item.id], driver: findDriver(snapshot, item.kind) }))
  const trouble = cards.some(({ check, driver }) => check === undefined || !check.ok || driver?.state !== 'ready')
  return make(id, trouble ? '!' : '✓', items.length)
}

/**
 * Какой таб открыть при входе на `/hardware` (п. 2.3): первый, где есть неудовлетворённая потребность.
 * Нет агента или драйвера — «Драйверы»; касса не включена — «Кассы»; касса работает, терминалов нет — «Терминалы»;
 * всё готово — «Кассы». Опрос таб не переключает никогда — функция вызывается один раз при входе.
 */
export function defaultTab(snapshot: EquipmentSnapshot): HardwareTabId {
  if (linkPhase(snapshot.link, snapshot.now) !== 'connected') return 'drivers'
  if (!categoryDriverReady(snapshot, 'fiscal-register')) return 'drivers'
  if (!kassaEnabled(snapshot)) return 'kassas'
  const noTerminals = snapshot.terminals !== null && snapshot.terminals.items.length === 0
  if (noTerminals) return 'terminals'
  return 'kassas'
}

export type NextStepId =
  | 'pick-agent'
  | 'copy-origin'
  | 'update-agent'
  | 'download-agent'
  | 'recheck'
  | 'download-driver'
  | 'check-drivers'
  | 'scan-kassa'
  | 'scan-terminal'

export interface NextStepVm {
  id: NextStepId
  /** Подпись главной кнопки вердикта, без «Дальше:». */
  label: string
  /** Целиком, как на кнопке: «Дальше: скачать драйвер АТОЛ →». */
  text: string
  tab: HardwareTabId
  /** Драйвер, к которому относится шаг («Скачать драйвер»). */
  driverId: string | null
}

function step(id: NextStepId, label: string, tab: HardwareTabId, driverId: string | null = null): NextStepVm {
  return { id, label, text: `Дальше: ${label.charAt(0).toLowerCase()}${label.slice(1)} →`, tab, driverId }
}

/**
 * «Дальше: …» (п. 2.4) — одна главная кнопка вердикта; `null`, когда делать нечего или шаг не от окна (смену открывают в РМК).
 * Терминал не обязателен: после включённой кассы предлагаем «Найти терминал» только если терминалов нет, а драйвер готов.
 */
export function nextStep(snapshot: EquipmentSnapshot): NextStepVm | null {
  const phase = linkPhase(snapshot.link, snapshot.now)
  switch (phase) {
    case 'searching':
      return null
    case 'choose':
      return step('pick-agent', 'Выбрать программу-драйвер', 'drivers')
    case 'denied':
      return step('copy-origin', 'Скопировать адрес сайта', 'drivers')
    case 'outdated':
      return step('update-agent', 'Скачать новую версию', 'drivers')
    case 'lost':
      return step('recheck', 'Проверить сейчас', 'drivers')
    case 'missing':
      return step('download-agent', 'Скачать программу-драйвер', 'drivers')
    case 'connected':
      break
  }
  const atol = findDriver(snapshot, ATOL_DRIVER_ID)
  if (atol === null) return null
  if (atol.state === 'stopped') return step('check-drivers', 'Проверить драйверы', 'drivers', ATOL_DRIVER_ID)
  if (atol.state !== 'ready') return step('download-driver', 'Скачать драйвер АТОЛ', 'drivers', ATOL_DRIVER_ID)
  if (!kassaEnabled(snapshot)) return step('scan-kassa', 'Найти кассу', 'kassas')
  const noTerminals = snapshot.terminals !== null && snapshot.terminals.items.length === 0
  if (noTerminals && categoryDriverReady(snapshot, 'payment-terminal')) return step('scan-terminal', 'Найти терминал', 'terminals')
  return null
}
